import type { ContentItemId } from '@kidshub/contracts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { ButtonLink, Card, EmptyState, Icon, type IconName } from '../ui/kit.js';
import { LiveBlock } from './live-block.js';
import { RunningImports } from './running-imports.js';
import './overview.css';

/**
 * The dashboard.
 *
 * A live block for every paired tablet, because a family has few of them and a
 * control for choosing between them would earn nothing. Below that, a download
 * still under way, and the places a parent goes next — each with the count
 * that tells them whether it needs a visit.
 *
 * What is deliberately absent: the board's ribbon of the day, the movement-break
 * and screen-proximity tallies, and the list of what was watched. None of those
 * exist in `LiveDeviceState`, and inventing them would be worse than leaving the
 * space empty — a parent acts on what a dashboard says. One line says so.
 */
export const OverviewPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const devices = useResource('devices', () => apiClient.devices.list());
  const children = useResource('children', () => apiClient.children.list());
  const content = useResource('content', () => apiClient.catalog.list());

  const deviceList = devices.resource.status === 'ready' ? devices.resource.data.devices : [];
  const childList = children.resource.status === 'ready' ? children.resource.data.children : [];
  const items = content.resource.status === 'ready' ? content.resource.data.items : [];

  const itemFor = (contentItemId: ContentItemId) =>
    items.find((item) => item.id === contentItemId) ?? null;

  const isLoading = devices.resource.status === 'loading' || children.resource.status === 'loading';
  const failure =
    devices.resource.status === 'failed'
      ? devices.resource.error
      : children.resource.status === 'failed'
        ? children.resource.error
        : null;

  const onlineCount = deviceList.filter((device) => device.isOnline).length;
  const approvedCount = items.filter((item) => item.approvedForChildIds.length > 0).length;

  // The day, in the parent's language: the dashboard is about today.
  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());

  return (
    <AppShell title={t('overview.title')} subtitle={today}>
      {isLoading ? <p className="t-meta">{t('common.loading')}</p> : null}
      {failure === null ? null : (
        <p className="help err" role="alert">
          {t(toErrorKey(failure))}
        </p>
      )}

      <div className="overview-live">
        {deviceList.map((device) => {
          const active = childList.find((entry) => entry.child.id === device.activeChildId) ?? null;

          return (
            <LiveBlock
              key={device.id}
              device={device}
              childName={active?.child.name ?? null}
              budget={active?.profile.budget ?? null}
              itemFor={itemFor}
              playable={
                active === null
                  ? []
                  : items.filter((item) => item.approvedForChildIds.includes(active.child.id))
              }
            />
          );
        })}

        {!isLoading && failure === null && deviceList.length === 0 ? (
          <EmptyState
            title={t('overview.noDevices')}
            actions={
              <ButtonLink variant="primary" icon="plus" to="/devices/pair">
                {t('devices.pairNew')}
              </ButtonLink>
            }
          />
        ) : null}
      </div>

      <div className="overview-grid">
        <RunningImports />

        <Card title={t('overview.shortcuts')} className="overview-shortcuts">
          <div className="overview-shortcut-list">
            <Shortcut
              to="/library/import"
              icon="plus"
              tone="amber"
              label={t('nav.addChannel')}
              detail={t('overview.addChannelDetail')}
            />
            <Shortcut
              to="/library"
              icon="lib"
              tone="sage"
              label={t('nav.library')}
              detail={t('library.subtitle', { total: items.length, approved: approvedCount })}
            />
            <Shortcut
              to="/children"
              icon="child"
              label={t('nav.children')}
              detail={
                childList.length === 0
                  ? t('overview.noChildren')
                  : childList.map((entry) => entry.child.name).join(', ')
              }
            />
            <Shortcut
              to="/devices"
              icon="tablet"
              label={t('nav.devices')}
              detail={t('overview.devicesSummary', {
                total: deviceList.length,
                online: onlineCount,
              })}
            />
          </div>
        </Card>
      </div>

      <p className="t-meta overview-later">{t('overview.historyLater')}</p>
    </AppShell>
  );
};

/** A place to go, with the one fact that says whether it needs a visit. */
const Shortcut = ({
  to,
  icon,
  tone,
  label,
  detail,
}: {
  to: string;
  icon: IconName;
  tone?: 'amber' | 'sage';
  label: string;
  detail: string;
}) => (
  <Link className="shortcut" to={to}>
    <span className={tone === undefined ? 'felt-sq' : `felt-sq ${tone}`}>
      <Icon name={icon} />
    </span>
    <span>
      <b>{label}</b>
      <span>{detail}</span>
    </span>
    <Icon name="chev" small />
  </Link>
);
